/**
 * What travels INSIDE the ciphertext (ADR-0027 D4.6) — the browser's half of a
 * format whose other half is `E2eContent.kt` in the KMP shared module.
 *
 * The wire is a byte, not a type. A payload beginning with `0x00` is a
 * versioned structured envelope — the marker, then UTF-8 JSON; anything else is
 * a bare UTF-8 body from a sender that predates the envelope. Both forms are
 * live on the wire right now, so both are decoded here forever.
 *
 * **This is a second implementation of a format with no compiler between the
 * ends** (dev-guide §9.0i). The Kotlin encoder is the producer, this is a
 * consumer, and nothing links them: the only thing that keeps them agreeing is
 * that `e2e-content.test.ts` asserts the same byte shapes
 * `E2eContentCodecTest.kt` does. A field added there and not here does not fail
 * to compile — it silently stops arriving on web.
 *
 * There is no ENCODER here on purpose. This browser cannot send an encrypted
 * message yet (D4.8 has no step for it), and an encoder with no caller is a
 * second producer nobody runs — which is how the two ends drift while every
 * gate stays green.
 *
 * **Decoding never throws and never loses the body.** A malformed structured
 * payload is read as plain text instead. Losing a reply target is cosmetic;
 * losing the message is not, and this is the last step before a human reads it.
 */

/** Marks a structured payload. Safe as a discriminator because a NUL cannot
 *  begin a body a text composer can produce — and even if one did, the
 *  fallback below renders it rather than dropping it. */
const STRUCTURED_MARKER = 0x00;

/** B9: the contact card a message can be, inside the envelope. */
export interface E2eContactCard {
  userId: string;
  username: string;
  displayName: string;
}

/**
 * One message of a HISTORY BUNDLE (ADR-0027 D4.7) — the phone catching this
 * browser up on what was said before it existed.
 *
 * Everything a live message takes from its envelope is restated here, and that
 * is the entire point of the type. A history envelope describes the RE-SEND:
 * its sender is the phone doing the syncing and its `created_at` is a moment
 * ago. Reading those would attribute every message in the conversation to the
 * person who linked the browser, and stack six months of talk under today's
 * date.
 */
export interface E2eHistoryFrame {
  /** The ORIGINAL author. */
  from: string;
  /** The ORIGINAL send time, ISO-8601. */
  at: string;
  /** 1-based position in the bundle, and the bundle's frozen size. On every
   *  frame rather than in a header, so progress works from whichever frame
   *  arrives first — a lost header would otherwise strand the receiver at
   *  "syncing" with no way to learn it had finished. */
  seq: number;
  total: number;
  /** `text` | `image` | `audio` | `video` | `file` | `sticker` | `contact`. */
  type: string;
  mediaUrl: string | null;
  thumbUrl: string | null;
  /** A11 — the ABSOLUTE deadline in epoch ms, or null. Absolute because a
   *  re-sent duration would hand a two-day-old disappearing message a fresh
   *  timer on this device. */
  expiresAt: number | null;
}

/** One decrypted message's content: a body plus whatever rode with it. */
export interface E2eContent {
  body: string;
  /** A8/A11-era metadata the SERVER must never see, which is why it is here
   *  and not in the frame. `replyToId` is a `client_msg_id` — the id both
   *  ends key an E2E message by, because there is no server row. */
  replyToId: string | null;
  forwarded: boolean;
  /** A11 — the sender's disappearing timer in seconds AT SEND TIME, so the
   *  reader enforces what the sender intended rather than the chat's current
   *  setting. */
  expireSeconds: number | null;
  /** B5 — "<pack>/<sticker>". Which drawing was sent is content. */
  stickerRef: string | null;
  contact: E2eContactCard | null;
  /** ADR-0027 D4.7 — set when this is one message of a history bundle rather
   *  than something just said. Its PRESENCE is the discriminator; there is no
   *  `kind` beside it, because two fields that must agree can disagree. */
  history: E2eHistoryFrame | null;
}

const EMPTY: Omit<E2eContent, "body"> = {
  replyToId: null,
  forwarded: false,
  expireSeconds: null,
  stickerRef: null,
  contact: null,
  history: null,
};

const utf8 = new TextDecoder();

/** Read a decrypted payload. Total: every input maps to a readable body. */
export function decodeContent(plaintext: Uint8Array): E2eContent {
  if (plaintext.length === 0) return { body: "", ...EMPTY };
  if (plaintext[0] !== STRUCTURED_MARKER) return { body: utf8.decode(plaintext), ...EMPTY };

  try {
    const json = JSON.parse(utf8.decode(plaintext.subarray(1))) as Record<string, unknown>;
    return {
      body: typeof json.body === "string" ? json.body : "",
      replyToId: str(json.reply_to),
      forwarded: json.forwarded === true,
      expireSeconds: typeof json.expire_seconds === "number" ? json.expire_seconds : null,
      stickerRef: str(json.sticker),
      contact: card(json.contact),
      history: frame(json.sync),
    };
  } catch {
    // Not the envelope it claimed to be. Show the bytes rather than nothing.
    return { body: utf8.decode(plaintext), ...EMPTY };
  }
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/**
 * A history frame is only a history frame when the four fields that make it
 * one are all present and usable.
 *
 * Half of one is worse than none: without `from` and `at` the message would be
 * stored under the syncing phone's name and today's date, which is a wrong
 * answer rather than a missing one, and the fallback (treat it as an ordinary
 * live message) at least renders something whose author the envelope actually
 * names.
 */
function frame(v: unknown): E2eHistoryFrame | null {
  if (v === null || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const from = str(o.from);
  const at = str(o.at);
  const seq = typeof o.seq === "number" ? o.seq : null;
  const total = typeof o.total === "number" ? o.total : null;
  if (from === null || at === null || seq === null || total === null) return null;
  const expiresAt = str(o.expires_at);
  const parsedExpiry = expiresAt === null ? NaN : Date.parse(expiresAt);
  return {
    from,
    at,
    seq,
    total,
    type: str(o.type) ?? "text",
    mediaUrl: str(o.media_url),
    thumbUrl: str(o.thumb_url),
    expiresAt: Number.isNaN(parsedExpiry) ? null : parsedExpiry,
  };
}

/** A card is only a card when all three fields are there. A half-decoded one
 *  would render as a contact row naming nobody. */
function card(v: unknown): E2eContactCard | null {
  if (v === null || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const userId = str(o.user_id);
  const username = str(o.username);
  const displayName = str(o.display_name);
  if (userId === null || username === null || displayName === null) return null;
  return { userId, username, displayName };
}
